/**
 * coach-service.js
 * Orchestrates coach prompt submissions through the shared domain use case.
 */

import { createSubmitCoachPrompt } from '../domain/coach/submit-coach-prompt.js';

export class CoachService {
  constructor({
    promptBuilder,
    coachGateway,
    teamGateway = null,
    debugGateway = null,
  } = {}) {
    if (typeof promptBuilder !== 'function') {
      throw new Error('promptBuilder is required');
    }
    if (!coachGateway || typeof coachGateway.submit !== 'function') {
      throw new Error('coachGateway with submit() is required');
    }

    this.buildPrecisionPrompt = promptBuilder;
    this.coachGateway = coachGateway;
    this.teamGateway = teamGateway;
    this.debugGateway = debugGateway;

    this.submitUseCase = createSubmitCoachPrompt({
      sendPrompt: (payload) => this.coachGateway.submit(payload),
      buildPrecisionPrompt: (...args) => this.buildPrecisionPrompt(...args),
      fetchTeamDetails: this.teamGateway?.fetchDetails
        ? (teamId) => this.teamGateway.fetchDetails(teamId)
        : async () => null,
    });
  }

  async submitPrompt(request = {}) {
    this.debugGateway?.onSubmitStart?.();

    try {
      const result = await this.submitUseCase(request);
      this.debugGateway?.onSubmitSuccess?.({ response: result.response });

      return {
        response: result.response,
        precisionPrompt: result.precisionPrompt,
        context: result.context,
        teamFetchMs: result.teamFetchMs || result.timings?.teamFetchMs,
        timings: result.timings,
        tokens: result.tokens,
      };
    } catch (error) {
      this.debugGateway?.onSubmitError?.({ error });
      throw error;
    }
  }
}

if (typeof window !== 'undefined') {
  window.AgileProServices = window.AgileProServices || {};
  window.AgileProServices.CoachService = CoachService;
}
